import {
  Button,
  CircularProgress,
  Link,
  QuoteStatusDropdownChip,
  Text,
  Toggle,
  Tooltip,
} from '@shared/components';
import { BaseUrlContext } from '@shared/contexts/BaseUrlContext';
import {
  QuoteBidStatus,
  QuoteHistorySummaryFragment,
  ShipmentHistorySummaryFragment,
} from '@shared/generated/graphql';
import {
  getBuiltInLaneHistoryDefaults,
  getLaneHistoryDefaults,
  getNextLaneHistoryLookbackPeriod,
  getPreviousLaneHistoryLookbackPeriod,
} from '@shared/hooks/laneHistoryDefaults';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import {
  LaneHistoryFilters,
  toBuiltInDefaultFilters,
} from '@shared/hooks/useLaneHistoryFilters';
import { usePlacesSuggestions } from '@shared/hooks/usePlacesSuggestions';
import { usePlatform } from '@shared/hooks/usePlatform';
import { Quote } from '@shared/types/quote';
import { sumCarrierRate } from '@shared/utils/rates/rates';
import { QueryParamName } from 'clerk_common/constants/urls/queryParams';
import { SHIPMENTS_ROUTE } from 'clerk_common/constants/urls/routes';
import { formatDateOnlyUtc } from 'clerk_common/stringification/dates';
import { formatDollars } from 'clerk_common/stringification/numbers';
import { formatPickupDate } from 'clerk_common/templates/freight_order/fieldExtractors/fieldExtractors';
import { CompletedFreightOrderTemplate } from 'clerk_common/templates/freight_order/types';
import { sumCustomerRate } from 'clerk_common/types';
import clsx from 'clsx';
import { differenceInDays } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { LuMinus, LuPlus, LuTruck } from 'react-icons/lu';
import {
  MdAdd,
  MdOpenInNew,
  MdOutlineFilterList,
  MdRemove,
} from 'react-icons/md';
import { PiEye } from 'react-icons/pi';
import { TbMessage2Dollar } from 'react-icons/tb';
import { getQuoteRoute } from '../../utils/getQuoteRoute';
import { AggregateRow, getMargin } from './components/AggregateRow';
import { FilterChips } from './FilterChips';
import { DefaultsEntry } from './LaneHistoryDefaults';
import { HistoryFilterType, LaneHistoryRowProps } from './types';
import { buildQuotesPageUrl } from './utils/buildQuotesPageUrl';

const MAX_LANE_HISTORY_ROWS = 5;

const RowCell = ({ children }: { children: React.ReactNode }) => {
  return <td className="px-1 py-1">{children}</td>;
};

const DateCell = ({ date }: { date: Date }) => {
  return (
    <RowCell>
      <div>
        <Text type="body-xs" className="whitespace-nowrap text-gray-500">
          {formatDateOnlyUtc(date)}
        </Text>
      </div>
    </RowCell>
  );
};

const QuoteRow = ({
  quoteStatus,
  buyRate,
  sellRate,
  pickupDate,
  entityId,
}: {
  quoteStatus?: string;
  pickupDate: Date;
  buyRate?: number;
  sellRate?: number;
  entityId: string;
}) => {
  const { url } = useContext(BaseUrlContext);
  const { isWeb } = usePlatform();
  const { track } = useAnalytics();
  const margin = getMargin({
    buyRate,
    sellRate,
  });
  const quoteUrl = getQuoteRoute(url, entityId);
  return (
    <tr>
      <RowCell>
        <Tooltip content="Quote">
          <div>
            <TbMessage2Dollar size={18} className="text-gray-700" />
          </div>
        </Tooltip>
      </RowCell>
      <RowCell>
        <div className="flex items-center justify-start gap-1">
          {isWeb ? (
            <Text type="body-xs" className="text-gray-700">
              Quote
            </Text>
          ) : null}
          {quoteStatus ? (
            <QuoteStatusDropdownChip
              minimal
              status={quoteStatus as QuoteBidStatus}
            />
          ) : null}
        </div>
      </RowCell>
      <RowCell>
        <Text className="text-gray-500" type="body-xs">
          {buyRate ? formatDollars(buyRate) : '--'}
        </Text>
      </RowCell>
      <RowCell>
        <Text className="text-gray-500" type="body-xs">
          {sellRate ? formatDollars(sellRate) : '--'}
        </Text>
      </RowCell>
      <RowCell>
        <Text className="text-center text-gray-500" type="body-xs">
          {margin ? `${margin?.toFixed(0)}%` : '--'}
        </Text>
      </RowCell>
      <DateCell date={pickupDate} />
      <RowCell>
        <Tooltip side="left" content={'View quote in Vooma'} isInstant>
          <div className="flex items-center justify-center">
            <Link
              href={quoteUrl}
              isExternal
              onPress={() => {
                track('Clicked Viewed Lane History Quote');
              }}
            >
              <PiEye size={18} />
            </Link>
          </div>
        </Tooltip>
      </RowCell>
      <RowCell>
        <Text className="text-center text-gray-500" type="body-xs">
          --
        </Text>
      </RowCell>
    </tr>
  );
};

const ShipmentRow = ({
  carrierName,
  pickupDate,
  buyRate,
  sellRate,
  externalLink,
  entityId,
}: {
  carrierName?: string;
  pickupDate: Date;
  buyRate?: number;
  sellRate?: number;
  externalLink?: string;
  entityId: string;
}) => {
  const { track } = useAnalytics();
  const { url } = useContext(BaseUrlContext);
  const { isWeb } = usePlatform();
  const margin = getMargin({
    buyRate,
    sellRate,
  });
  const shipmentUrl = getShipmentUrl(url, entityId);
  return (
    <tr>
      <RowCell>
        <Tooltip content={`Shipment via ${carrierName || 'N/A'}`}>
          <div>
            <LuTruck size={18} className="text-gray-700" />
          </div>
        </Tooltip>
      </RowCell>
      <RowCell>
        <Tooltip content={`Shipment via ${carrierName || 'N/A'}`}>
          <div className="flex items-center justify-start gap-1">
            <Text
              type="body-xs"
              className={clsx(
                !isWeb && 'max-w-[65px] truncate',
                'max-w-[100px] truncate text-gray-700'
              )}
            >
              {carrierName || 'N/A'}
            </Text>
          </div>
        </Tooltip>
      </RowCell>
      <RowCell>
        <Text className="text-gray-500" type="body-xs">
          {buyRate ? formatDollars(buyRate) : '--'}
        </Text>
      </RowCell>
      <RowCell>
        <Text className="text-gray-500" type="body-xs">
          {sellRate ? formatDollars(sellRate) : '--'}
        </Text>
      </RowCell>
      <RowCell>
        <Text className="text-center text-gray-500" type="body-xs">
          {margin ? `${margin?.toFixed(0)}%` : '--'}
        </Text>
      </RowCell>
      <DateCell date={pickupDate} />
      <RowCell>
        <Tooltip side="left" content={'View shipment in Vooma'} isInstant>
          <div className="flex items-center justify-center">
            <Link
              href={shipmentUrl}
              isExternal
              onPress={() => {
                track('Clicked Viewed Lane History Shipment in Vooma');
              }}
            >
              <PiEye size={18} />
            </Link>
          </div>
        </Tooltip>
      </RowCell>
      <RowCell>
        {externalLink ? (
          <Tooltip side="left" content={'View shipment in TMS'} isInstant>
            <div>
              <Link
                href={externalLink}
                isExternal
                onPress={() => {
                  track('Clicked Viewed Lane History Shipment in TMS');
                }}
              >
                <MdOpenInNew size={18} />
              </Link>
            </div>
          </Tooltip>
        ) : (
          <Text className="text-center text-gray-500" type="body-xs">
            --
          </Text>
        )}
      </RowCell>
    </tr>
  );
};

const LaneHistoryRow = (p: LaneHistoryRowProps) => {
  return p.rowType === 'QUOTE' ? <QuoteRow {...p} /> : <ShipmentRow {...p} />;
};

const quoteFragmentToLaneHistoryRow = (
  quote: QuoteHistorySummaryFragment
): LaneHistoryRowProps => {
  return {
    entityId: quote.id,
    quoteStatus: quote.status,
    pickupDate: getDateFromQuote(quote),
    rowType: 'QUOTE',
    sellRate: quote.customerRate
      ? sumCustomerRate(quote.customerRate)
      : undefined,
    buyRate: quote.carrierRate ? sumCarrierRate(quote.carrierRate) : undefined,
  };
};

const shipmentFragmentToLaneHistoryRow = (
  shipment: ShipmentHistorySummaryFragment
): LaneHistoryRowProps[] => {
  return (
    shipment.carrierRates
      ?.filter(
        (carrierRate) =>
          !isNil(carrierRate.pickupAt) &&
          !isNil(carrierRate.buyRate) &&
          !isNil(carrierRate.sellRate)
      )
      .map((carrierRate) => {
        return {
          entityId: shipment.id,
          carrierName: carrierRate.name,
          pickupDate: new Date(carrierRate.pickupAt ?? ''),
          rowType: 'SHIPMENT',
          buyRate: carrierRate.buyRate ?? undefined,
          sellRate: carrierRate.sellRate ?? undefined,
          externalLink: shipment.externalDeepLink ?? undefined,
        };
      }) ?? []
  );
};

export const getShipmentUrl = (webBaseUrl?: string, shipmentId?: string) => {
  return `${webBaseUrl}${SHIPMENTS_ROUTE}?${QueryParamName.SHIPMENT_ID}=${shipmentId}`;
};

interface QuoteLaneHistoryProps {
  webBaseUrl: string;
  quotes: QuoteHistorySummaryFragment[];
  shipments: ShipmentHistorySummaryFragment[];
  quoteId: string;
  quote: Quote;
  filters: LaneHistoryFilters;
  loading?: boolean;
  setFilters: (filters: LaneHistoryFilters) => void;
  resetFilters: () => void;
  navigateToQuote: (quoteId: string) => void;
}

const getDateFromQuote = (quote: QuoteHistorySummaryFragment): Date => {
  const extractedData = quote.extractedData as CompletedFreightOrderTemplate;
  const pickupDate = formatPickupDate(extractedData);
  const createdDate = quote.createdAt;
  return new Date(pickupDate ?? createdDate);
};

const getQuoteLaneHistoryRowData = (
  quotes: QuoteHistorySummaryFragment[],
  shipments: ShipmentHistorySummaryFragment[]
): LaneHistoryRowProps[] => {
  const allRows: LaneHistoryRowProps[] = [];
  for (const s of shipments) {
    allRows.push(...shipmentFragmentToLaneHistoryRow(s));
  }

  allRows.push(
    ...quotes.map((q: QuoteHistorySummaryFragment) =>
      quoteFragmentToLaneHistoryRow(q)
    )
  );

  const sortedRows = allRows.sort((a, b) => {
    return b.pickupDate.getTime() - a.pickupDate.getTime();
  });

  return sortedRows;
};

const getDateFilteredRows = (
  rows: LaneHistoryRowProps[],
  sinceDaysAgo?: number
): LaneHistoryRowProps[] => {
  return sinceDaysAgo
    ? rows.filter((row) => {
        return differenceInDays(new Date(), row.pickupDate) <= sinceDaysAgo;
      })
    : rows;
};

const TableHeaderCell = ({
  title,
  hidden,
}: {
  title: string;
  hidden: boolean;
}) => {
  return (
    <th className={'text-left text-gray-700'}>
      {hidden ? null : (
        <Text type="body-xs" isHeavy>
          {title}
        </Text>
      )}
    </th>
  );
};

export const QuoteLaneHistory = (p: QuoteLaneHistoryProps) => {
  const laneHistoryDefaults = getLaneHistoryDefaults(p.quote.organization.id);
  const builtInLaneHistoryDefaults = getBuiltInLaneHistoryDefaults();
  const [expanded, setExpanded] = useState(true);
  const [rowLimit, setRowLimit] = useState<number | undefined>(
    MAX_LANE_HISTORY_ROWS
  );
  const { getSuggestions } = usePlacesSuggestions();
  const { track } = useAnalytics();
  const [historyFilter, setHistoryFilter] = useState<HistoryFilterType>('all');
  const [aggregateRows, setAggregateRows] = useState<number>(1);
  const handleToggleHistory = () => {
    const event = expanded ? 'Collapse Lane History' : 'Expand Lane History';
    setExpanded(!expanded);
    track(event, {
      quoteId: p.quoteId,
    });
  };
  const [sinceDaysAgo, setSinceDaysAgo] = useState<number>(
    laneHistoryDefaults.sinceDaysAgo
  );

  useEffect(() => {
    setSinceDaysAgo(
      getLaneHistoryDefaults(p.quote.organization.id).sinceDaysAgo
    );
  }, [p.quote.id, p.quote.organization.id]);

  const handleOpenLaneHistory = async () => {
    const baseUrl = getQuoteRoute(p.webBaseUrl, p.quoteId);
    const shouldOpenNewWindow = Boolean(p.webBaseUrl);

    const laneHistoryRoute = await buildQuotesPageUrl({
      baseUrl,
      quote: p.quote,
      getSuggestions,
    });

    track('Opened Filtered Lane History View', {
      quoteId: p.quoteId,
      data: { filteredUrl: laneHistoryRoute },
    });

    if (shouldOpenNewWindow) {
      window.open(laneHistoryRoute, '_blank');
    } else {
      window.location.search = laneHistoryRoute;
    }
  };

  const rowsWithData = getQuoteLaneHistoryRowData(p.quotes, p.shipments);
  const historyRows = getDateFilteredRows(rowsWithData, sinceDaysAgo);
  const filteredHistoryRows = historyRows.filter((row) => {
    if (historyFilter === 'quotes') {
      return row.rowType === 'QUOTE';
    } else if (historyFilter === 'shipments') {
      return row.rowType === 'SHIPMENT';
    } else {
      return true;
    }
  });
  const quoteCount = historyRows.filter(
    (row) => row.rowType === 'QUOTE'
  ).length;
  const shipmentCount = historyRows.filter(
    (row) => row.rowType === 'SHIPMENT'
  ).length;
  const hasShipmentsAndQuotes = shipmentCount > 0 && quoteCount > 0;
  const handleToggleLaneHistory = () => {
    track('Toggled Lane History Length', {
      quoteId: p.quoteId,
      data: {
        totalRows: filteredHistoryRows.length,
        showMore: rowLimit ? true : false,
      },
    });
    setRowLimit((c) => (c ? undefined : MAX_LANE_HISTORY_ROWS));
  };

  const stepSinceDaysAgo = (direction: 'previous' | 'next') => {
    track('Stepped Lane History Date Filter', {
      quoteId: p.quoteId,
      data: { direction, sinceDaysAgo },
    });
    setSinceDaysAgo((c) => {
      return direction === 'next'
        ? getNextLaneHistoryLookbackPeriod(c)
        : getPreviousLaneHistoryLookbackPeriod(c);
    });
  };

  const resetFilters = () => {
    setHistoryFilter('all');
    setSinceDaysAgo(builtInLaneHistoryDefaults.sinceDaysAgo);
    p.setFilters(toBuiltInDefaultFilters(p.quote));
  };

  return (
    <div className="flex flex-col p-4">
      <>
        <div
          onClick={handleToggleHistory}
          className="flex cursor-pointer flex-row items-center justify-between text-gray-600"
        >
          <div className="flex flex-row items-center gap-2">
            <Text type="body-md">Lane history</Text>
            <Tooltip
              content={
                p.webBaseUrl
                  ? 'Open Vooma web app filtered to this lane'
                  : 'Filter quotes to this lane'
              }
            >
              <div
                onClick={handleOpenLaneHistory}
                className="text-brand-500 cursor-pointer"
              >
                {p.webBaseUrl ? <MdOpenInNew /> : <MdOutlineFilterList />}
              </div>
            </Tooltip>
          </div>
          <Tooltip
            isInstant
            content={expanded ? 'Collapse lane history' : 'Expand lane history'}
          >
            {expanded ? <LuMinus size={16} /> : <LuPlus size={16} />}
          </Tooltip>
        </div>
        {expanded && (
          <div className="mt-2 flex cursor-pointer flex-col gap-2">
            <LaneHistoryDescription filters={p.filters} />
            <div className="flex flex-row items-center justify-between">
              <DefaultsEntry
                filters={p.filters}
                laneHistoryDefaults={laneHistoryDefaults}
                resetLaneHistoryDefaults={builtInLaneHistoryDefaults}
                organizationId={p.quote.organization.id}
                setFilters={p.setFilters}
                setSinceDaysAgo={setSinceDaysAgo}
              />

              <Button
                onPress={resetFilters}
                className="my-1"
                variant="tertiary"
                size="small"
              >
                Reset filters
              </Button>
            </div>
            <FilterChips filters={p.filters} setFilters={p.setFilters} />
            <div className="justify-betwee flex flex-row flex-wrap items-center gap-2 p-[2px] text-gray-700">
              {hasShipmentsAndQuotes ? (
                <Tooltip
                  side="left"
                  content={`Filter to shipments (buy rates),\nquotes (sell rates), or both`}
                >
                  <div>
                    <Toggle
                      value={historyFilter}
                      onValueChange={(value: string) => {
                        if (!value) return;
                        track('Toggled Lane History Entity', {
                          quoteId: p.quoteId,
                          data: { historyFilter: value },
                        });
                        setHistoryFilter(value as HistoryFilterType);
                      }}
                    >
                      {[
                        {
                          label: `Shipments (${shipmentCount})`,
                          value: 'shipments',
                        },
                        {
                          label: `Quotes (${quoteCount})`,
                          value: 'quotes',
                        },
                        {
                          label: `Both (${historyRows.length})`,
                          value: 'all',
                        },
                      ]}
                    </Toggle>
                  </div>
                </Tooltip>
              ) : null}
              <div className="flex flex-row flex-wrap items-center justify-center gap-2 rounded-full bg-gray-200 px-1 py-[2px] shadow-sm hover:bg-gray-100">
                <div
                  className="cursor-pointer p-1"
                  onClick={() => stepSinceDaysAgo('previous')}
                >
                  <MdRemove size={12} />
                </div>
                <div className="pt-[2px]">
                  <span className="flex flex-row items-center gap-1">
                    <Text type="body-xs" className="text-gray-700">
                      Last{' '}
                    </Text>
                    <Text type="body-xs" className="text-gray-700" isHeavy>
                      {sinceDaysAgo} days
                    </Text>
                  </span>
                </div>
                <div
                  className="cursor-pointer p-1"
                  onClick={() => stepSinceDaysAgo('next')}
                >
                  <MdAdd size={12} />
                </div>
              </div>
            </div>
            {p.loading ? (
              <CircularProgress size="small" className="self-center" />
            ) : null}
            {!p.loading &&
              (isEmpty(filteredHistoryRows) ? (
                <Text type="body-xs" className="text-gray-400">
                  No shipments or quotes match the current filters
                </Text>
              ) : (
                <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3">
                  <div className="flex max-h-[240px] flex-col overflow-y-scroll">
                    <table>
                      <tr>
                        <TableHeaderCell title="Icon" hidden />
                        <TableHeaderCell title="Source" hidden={true} />
                        <TableHeaderCell title="Buy" hidden={false} />
                        <TableHeaderCell title="Sell" hidden={false} />
                        <TableHeaderCell title="Margin" hidden={false} />
                        <TableHeaderCell title="Date" hidden={false} />
                        <TableHeaderCell title="View in Vooma" hidden />
                        <TableHeaderCell title="Open external" hidden />
                      </tr>
                      {Array.from({ length: aggregateRows }, (_, i) => (
                        <AggregateRow
                          key={i}
                          rows={historyRows}
                          onRemove={
                            i === 0
                              ? undefined
                              : () => {
                                  track('Removed Lane History Aggregate Row', {
                                    quoteId: p.quoteId,
                                  });
                                  setAggregateRows(aggregateRows - 1);
                                }
                          }
                          onAdd={
                            i === aggregateRows - 1
                              ? () => {
                                  track('Added Lane History Aggregate Row', {
                                    quoteId: p.quoteId,
                                  });
                                  setAggregateRows(aggregateRows + 1);
                                }
                              : undefined
                          }
                        />
                      ))}

                      {filteredHistoryRows
                        .slice(0, rowLimit)
                        .map((row, index) => {
                          return (
                            <LaneHistoryRow key={`history-${index}`} {...row} />
                          );
                        })}
                    </table>
                    {filteredHistoryRows.length > MAX_LANE_HISTORY_ROWS && (
                      <div
                        onClick={handleToggleLaneHistory}
                        className="text-brand-500 cursor-pointer text-sm hover:underline"
                      >
                        <Text type="body-xs">
                          {rowLimit
                            ? `Show ${
                                filteredHistoryRows.length -
                                MAX_LANE_HISTORY_ROWS
                              } more`
                            : 'Show fewer'}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </>
    </div>
  );
};

type LaneHistoryDescriptionProps = {
  filters: LaneHistoryFilters;
};

function LaneHistoryDescription(p: LaneHistoryDescriptionProps) {
  const originRadius = p.filters.laneFilter?.origin?.radiusMiles;
  const originRadiusString = originRadius ? `${originRadius} mi` : '';
  const destinationRadius = p.filters.laneFilter?.destination?.radiusMiles;
  const destinationRadiusString = destinationRadius
    ? `${destinationRadius} mi`
    : '';
  const hasRadius =
    !isEmpty(originRadiusString) && !isEmpty(destinationRadiusString);
  const sameRadius = originRadius === destinationRadius;

  let str = 'Recent quotes and shipments';
  if (hasRadius) {
    if (sameRadius) {
      str += ` within ${originRadiusString} of origin and destination`;
    } else {
      str += ` within ${originRadiusString} of origin, ${destinationRadiusString} of destination`;
    }
  }

  return (
    <div className="flex cursor-default flex-row items-center gap-2">
      <Text type="body-sm" className="text-gray-500">
        {str}
      </Text>
    </div>
  );
}
